/**
 * Side-by-side option boxes for single_choice (spec 111-01 §1). The question text stays stored
 * whole - results, exports and twins read the full wording - so the boxes exist only in this parse.
 */

import type { ParsedQuestion } from '../../components/survey/types';

export interface ChoiceBoxSplit {
  intro: string;
  boxes: Record<string, string[]>;
}

type SideBySideQuestion = Partial<
  Pick<ParsedQuestion, 'type' | 'choices' | 'side_by_side_choices' | 'media_urls' | 'option_media'>
>;

const SIDE_BY_SIDE_TYPE = 'single_choice';
const MIN_BOXES = 2;
const LITERAL_NEWLINE = /\\n/g;
const PARAGRAPH_BREAK = /\n\s*\n/;
const PARAGRAPH_JOIN = '\n\n';
// A dash or star is a bullet only with a space after it, so "-5% fat" keeps its sign.
const BULLET = /^(?:•|[-*](?=\s))\s*/;

const TWO_PER_ROW = 'grid-cols-2';
const COLUMNS_FROM_MD: Record<number, string> = {
  3: `${TWO_PER_ROW} md:grid-cols-3`,
  4: `${TWO_PER_ROW} md:grid-cols-4`,
};
const MAX_COLUMNS = 4;

const normalise = (value: string) => value.trim().toLowerCase();

export function splitChoiceBoxes(text: string, choices: string[]): ChoiceBoxSplit | null {
  const keys = new Map(choices.map((choice): [string, string] => [normalise(choice), choice]));
  if (choices.length < MIN_BOXES || keys.size !== choices.length) return null;

  const paragraphs = (text ?? '')
    .replace(LITERAL_NEWLINE, '\n')
    .split(PARAGRAPH_BREAK)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  const intro: string[] = [];
  const boxes: Record<string, string[]> = {};
  for (const paragraph of paragraphs) {
    const [first, ...rest] = paragraph.split('\n');
    const choice = keys.get(normalise(first));
    if (choice === undefined) {
      if (Object.keys(boxes).length > 0) return null;
      intro.push(paragraph);
      continue;
    }
    if (choice in boxes) return null;
    boxes[choice] = rest.map((line) => line.trim().replace(BULLET, '')).filter(Boolean);
  }

  if (!choices.every((choice) => choice in boxes)) return null;
  return { intro: intro.join(PARAGRAPH_JOIN), boxes };
}

/** Single choice with no media of any kind: the only layout the boxes replace. */
export function offersSideBySideChoices(question: SideBySideQuestion | null | undefined): boolean {
  return (
    question?.type === SIDE_BY_SIDE_TYPE &&
    !question.media_urls?.length &&
    Object.keys(question.option_media ?? {}).length === 0
  );
}

/** The boxes for `text` - what the header would show - or null for today's plain list. */
export function sideBySideChoiceSplit(
  question: SideBySideQuestion | null | undefined,
  text: string | null | undefined,
): ChoiceBoxSplit | null {
  if (!question?.side_by_side_choices || !offersSideBySideChoices(question)) return null;
  return splitChoiceBoxes(text ?? '', question.choices ?? []);
}

export function choiceBoxGridClass(count: number): string {
  if (count <= MIN_BOXES) return TWO_PER_ROW;
  return COLUMNS_FROM_MD[Math.min(count, MAX_COLUMNS)];
}
